import React, { createContext, useCallback, useRef, useState } from 'react'
import LocalStorage from 'utils/myLocalStorage'

type LoadingContext = {
  isLoading: boolean
  handleLoadInProgress: (l: boolean) => void
}

const LoadingContext = createContext<LoadingContext>({
  isLoading: false,
  handleLoadInProgress: () => {},
})

export const LoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const activeRequests = useRef(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const DEBOUNCE_DELAY = 300 // délai avant d’éteindre le loading

  const handleLoadInProgress = useCallback((isLoadingActive: boolean) => {
    if (isLoadingActive) {
      // 🔵 Une nouvelle requête démarre
      activeRequests.current += 1

      LocalStorage.put([LocalStorage.KEY_TIMESTAMP_LAST_SERVER_CALL], new Date().getTime())

      // Si un timeout d’arrêt était prévu, on l’annule
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      // On force l’affichage immédiat du loading
      setIsLoading(true)
    } else {
      // 🔴 Une requête se termine
      activeRequests.current = Math.max(0, activeRequests.current - 1)

      // Si plus aucune requête active → on attend avant de désactiver
      if (activeRequests.current === 0) {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
          // On re-vérifie qu'aucune requête n’a redémarré
          if (activeRequests.current === 0) {
            setIsLoading(false)
          }
        }, DEBOUNCE_DELAY)
      }
    }
  }, [])

  return <LoadingContext.Provider value={{ isLoading, handleLoadInProgress }}>{children}</LoadingContext.Provider>
}

export default LoadingContext
